// Phase 85.A.121 — Non-dismissable modal cho VĐV khi admin yêu cầu nộp
// video chấm điểm trình.
//
// Phase 85.A.145 — Đã move `RatingVideoUploadModal` ra cấp context (gate).
// Modal này chỉ còn nhiệm vụ: hiện UI + accept/defer + callback `onAccepted`
// để gate parent mở upload modal (sống độc lập khỏi proposal modal).
import { useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { FilmReel, ArrowRight, Spinner, Info, Clock, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { parseZaloContact } from "../../lib/zaloHelpers";

// v16.17 Phase C.14 — Zalo Wikipedia icon URL (giống ZaloFloatButton).
const ZALO_ICON_URL = "https://upload.wikimedia.org/wikipedia/commons/9/91/Icon_of_Zalo.svg";

const CHIP_LABEL = {
  mismatch: "Điểm trình chưa khớp thực tế",
  post_tournament: "Cần xác nhận lại sau giải",
  complaint: "Có khiếu nại từ đối thủ",
  other: "Lý do khác",
};

export default function RatingVideoProposalRequiredModal({ proposal, onAccepted, onResolved }) {
  const [acting, setActing] = useState(null); // "accepted" | "deferred" | null

  if (!proposal) return null;

  const accept = async () => {
    if (acting) return;
    setActing("accepted");
    try {
      await api.patch(`/athlete/rating-video-proposals/${proposal.id}`, {
        decision: "accepted",
      });
      onAccepted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Không xử lý được yêu cầu. Thử lại sau.");
      setActing(null);
    }
  };

  // Phase 90.C.rvp-defer — Bật lại "Tạm hoãn 24h" theo user feedback.
  // BE set `defer_until = now + 24h` → popup ẩn 24h, sau đó hiện lại.
  const defer = async () => {
    if (acting) return;
    setActing("deferred");
    try {
      await api.patch(`/athlete/rating-video-proposals/${proposal.id}`, {
        decision: "deferred",
      });
      toast.success("Đã tạm hoãn 24h — hệ thống sẽ hiện lại sau.");
      onResolved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Không xử lý được yêu cầu. Thử lại sau.");
      setActing(null);
    }
  };

  const chips = Array.isArray(proposal.reason_chips) ? proposal.reason_chips : [];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[470] flex items-center justify-center bg-black/85 backdrop-blur-md px-3"
        data-testid="rating-video-proposal-modal"
      >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ duration: 0.22 }}
              className="relative w-full max-w-md rounded-2xl border border-violet-400/30 bg-[#0F1623] shadow-2xl overflow-hidden"
            >
              <div className="px-4 py-3 flex items-start gap-3 border-b border-white/10 bg-gradient-to-r from-violet-500/15 to-fuchsia-500/10">
                <div className="w-10 h-10 rounded-xl bg-violet-500/25 border border-violet-400/50 flex items-center justify-center shrink-0 text-violet-200">
                  <FilmReel size={18} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-violet-300/90">
                    Yêu cầu từ Admin
                  </div>
                  <div className="text-white text-[14px] font-black truncate mt-0.5">
                    Nộp video chấm điểm trình mới
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-3">
                {chips.length > 0 && (
                  <div>
                    <div className="text-[10.5px] font-bold text-white/65 uppercase tracking-wider mb-1.5">
                      Lý do
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {chips.map((c) => (
                        <span
                          key={c}
                          className="px-2.5 py-1 rounded-full bg-violet-500/15 border border-violet-400/35 text-violet-100 text-[10.5px] font-bold"
                          data-testid={`rating-video-proposal-chip-${c}`}
                        >
                          {CHIP_LABEL[c] || c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {proposal.reason ? (
                  <div className="px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-white/85 text-[12px] leading-relaxed whitespace-pre-wrap">
                    {proposal.reason}
                  </div>
                ) : null}
                <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-violet-500/[0.08] border border-violet-400/20">
                  <Info size={12} weight="bold" className="text-violet-300 shrink-0 mt-0.5" />
                  <div className="text-[10.5px] text-violet-100/90 leading-snug">
                    Vui lòng quay video bài đánh (chính diện, sáng rõ) và nộp để BTC xác thực điểm trình.
                    Thông báo sẽ hiện lại cho tới khi bạn nộp xong video (hoặc chọn tạm hoãn 24h).
                  </div>
                </div>

                {/* v16.17 Phase C.14 — Zalo BTC list (snapshot admin đính kèm).
                    Style GIỐNG Zalo Float: icon 44×44, mỗi dòng riêng biệt,
                    click mở zalo.me; nếu có SĐT thì hiện thêm nút gọi tel:. */}
                {Array.isArray(proposal.verify_zalos) && proposal.verify_zalos.length > 0 && (
                  <div
                    className="rounded-2xl border border-[#0068ff]/30 bg-[#0068ff]/[0.08] p-3 space-y-2"
                    data-testid="rating-video-proposal-zalo-btc-list"
                  >
                    <div className="text-[11.5px] font-black text-blue-200 leading-snug">
                      VĐV vui lòng liên hệ BTC để xác minh điểm trình
                    </div>
                    <div className="space-y-1.5">
                      {proposal.verify_zalos.map((z, i) => {
                        const parsed = parseZaloContact(z.zalo_href);
                        const phone = parsed?.phone || null;
                        return (
                          <div
                            key={i}
                            className="flex items-center gap-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 p-2 transition"
                            data-testid={`rating-video-proposal-zalo-item-${i}`}
                          >
                            <a
                              href={z.zalo_href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-11 h-11 rounded-full bg-[#0068ff] flex items-center justify-center shrink-0 shadow-[0_2px_10px_rgba(0,104,255,0.4)] hover:brightness-110 transition"
                              title={`Chat Zalo ${z.name}`}
                              data-testid={`rating-video-proposal-zalo-avatar-${i}`}
                            >
                              <img src={ZALO_ICON_URL} alt="Zalo" className="w-6 h-6" />
                            </a>
                            <div className="flex-1 min-w-0">
                              <div className="text-[12.5px] font-black text-white truncate">
                                {z.name}
                              </div>
                              <div className="text-[10.5px] text-white/55 truncate">
                                {z.role}
                                {z.tournament_name ? ` · ${z.tournament_name}` : ""}
                              </div>
                              {phone && (
                                <div className="text-[10.5px] text-blue-200/75 font-mono tabular-nums mt-0.5">
                                  {phone}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <a
                                href={z.zalo_href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-8 px-2.5 rounded-lg bg-[#0068ff]/25 hover:bg-[#0068ff]/40 border border-[#0068ff]/50 text-[10.5px] font-black text-blue-100 inline-flex items-center gap-1 transition"
                                title={`Chat Zalo ${z.name}`}
                                data-testid={`rating-video-proposal-zalo-chat-${i}`}
                              >
                                Zalo <ArrowRight size={10} weight="bold" />
                              </a>
                              {phone && (
                                <a
                                  href={`tel:${phone}`}
                                  className="h-8 w-8 rounded-lg bg-emerald-500/25 hover:bg-emerald-500/40 border border-emerald-400/50 text-emerald-100 inline-flex items-center justify-center transition"
                                  title={`Gọi ${z.name}`}
                                  data-testid={`rating-video-proposal-zalo-call-${i}`}
                                >
                                  <Phone size={12} weight="fill" />
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="text-[10px] text-white/45 text-right">
                  Gửi bởi: {proposal.proposed_by_name || "Admin"}
                </div>
              </div>

              <div className="px-4 py-3 bg-black/30 border-t border-white/10 flex items-center gap-2">
                {/* Phase 90.C.rvp-defer — Nút "Tạm hoãn 24h" để VĐV không bị
                    kẹt popup khi chưa tiện quay video. */}
                <button
                  type="button"
                  onClick={defer}
                  disabled={!!acting}
                  className="h-10 px-3 rounded-lg bg-white/[0.06] hover:bg-white/[0.10] text-white/85 text-[12px] font-bold border border-white/12 inline-flex items-center justify-center gap-1.5 disabled:opacity-40 transition"
                  data-testid="rating-video-proposal-defer"
                >
                  {acting === "deferred" ? (
                    <Spinner size={12} weight="bold" className="animate-spin" />
                  ) : (
                    <Clock size={12} weight="bold" />
                  )}
                  Tạm hoãn 24h
                </button>
                <button
                  type="button"
                  onClick={accept}
                  disabled={!!acting}
                  className="flex-1 h-10 px-4 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:brightness-110 text-white text-[12.5px] font-black inline-flex items-center justify-center gap-1.5 disabled:opacity-50 transition shadow-lg shadow-violet-500/30"
                  data-testid="rating-video-proposal-accept"
                >
                  {acting === "accepted" ? <Spinner size={12} weight="bold" className="animate-spin" /> : <FilmReel size={12} weight="fill" />}
                  Nộp video ngay
                  {!acting && <ArrowRight size={12} weight="bold" />}
                </button>
              </div>
            </motion.div>
          </motion.div>
      </AnimatePresence>
  );
}
